import { Box, Button, NotificationVariants, PillGroup } from '@/common/components';
import CampaignList from './components/CampaignList/CampaignList';
import { useTranslation } from 'react-i18next';
import { STUDY_INSIGHTS_TRANSLATION } from '../../../constants';
import { getTitles } from './components/Campaign.helper';
import { Pill } from '../shared/types/PillIds';
import { CampaignsPillId } from '@/modules/study-insights/types';
import { useState } from 'react';
import { ICampaign, Campaign } from '@/modules/study-insights/services/campaign';
import { useAddNotification } from '@/modules/notifications/store';
import { CampaignOverview } from './components/CampaignOverview';
import { EditCampaignModal } from './components/EditCampaingModal';
import { CampaignTrackingOverviewData } from '@/modules/study-insights/services/campaign/tracking';
import { NoActiveCampaignsBox } from './components/NoActiveCampaignsBox';
import { DateGroupOptionId } from './components/CampaignOverview/components/StackedBarChart';
import { UrlCampaignModal } from './components/UrlCampaignModal/UrlCampaignModal';

const getPillList = (titles: { activeCampaigns: string; inactiveCampaigns: string }): Pill[] => {
  return [
    {
      label: titles.activeCampaigns,
      id: CampaignsPillId.ACTIVE_CAMPAIGNS,
    },
    {
      label: titles.inactiveCampaigns,
      id: CampaignsPillId.INACTIVE_CAMPAIGNS,
    },
  ];
};

interface CampaignTrackingContentProps {
  isLoadingCampaignList?: boolean;
  isLoadingCampaignTrackingOverview?: boolean;
  id: string;
  campaigns: Campaign[];
  onOpenCreateCampaignModal?: () => void;
  onSuccessfulEditCampaign?: () => void;
  onSelectPill?: (id: CampaignsPillId) => void;
  onDateGroupOptionChange: (value: DateGroupOptionId) => void;
  selectedPill: CampaignsPillId;
  campaignTrackingOverviewData?: CampaignTrackingOverviewData;
  selectedDateGroupOption: DateGroupOptionId;
}

export const CampaignTrackingContent = ({
  id,
  isLoadingCampaignList,
  isLoadingCampaignTrackingOverview,
  campaigns,
  onOpenCreateCampaignModal,
  onSuccessfulEditCampaign,
  onSelectPill,
  onDateGroupOptionChange,
  selectedPill,
  campaignTrackingOverviewData,
  selectedDateGroupOption,
}: CampaignTrackingContentProps): JSX.Element => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const titles = getTitles({ t });
  const addNotification = useAddNotification();
  const [selectedCampaignToEdit, setSelectedCampaignToEdit] = useState<ICampaign | null>(null);
  const [selectedCampaignToCopyUrl, setSelectedCampaignToCopyUrl] = useState<ICampaign | null>(null);

  const handleOnClickPill = (id: string): void => {
    if (onSelectPill) {
      onSelectPill(id as CampaignsPillId);
    }
  };

  const handleSelectCampaignToEdit = (campaign: ICampaign): void => {
    setSelectedCampaignToEdit(campaign);
  };

  const handleCloseEditModal = (): void => {
    setSelectedCampaignToEdit(null);
  };

  const handleSelectCampaignUrl = (campaign: ICampaign): void => {
    setSelectedCampaignToCopyUrl(campaign);
  };

  const handleCloseUrlCampaignModal = (): void => {
    setSelectedCampaignToCopyUrl(null);
  };

  const handleEditCampaignSuccess = (): void => {
    addNotification({
      title: t('edit_campaign.success_notification'),
      type: NotificationVariants.SUCCESS,
      autoClose: true,
    });
    setSelectedCampaignToEdit(null);
    if (onSuccessfulEditCampaign) {
      onSuccessfulEditCampaign();
    }
  };

  const handleEditCampaignError = (): void => {
    addNotification({
      title: t('edit_campaign.error_notification'),
      type: NotificationVariants.ERROR,
      autoClose: true,
    });
  };

  const shouldShowEmptyState = (() => {
    if (isLoadingCampaignTrackingOverview) {
      return false;
    }

    if (
      campaignTrackingOverviewData?.data.campaigns?.length &&
      campaignTrackingOverviewData.data.campaigns.length > 0
    ) {
      return false;
    }

    return true;
  })();

  if (shouldShowEmptyState) {
    return <NoActiveCampaignsBox t={t} onOpenCreateCampaignModal={onOpenCreateCampaignModal} />;
  }

  return (
    <Box data-testid={`${id}-content-container`}>
      <CampaignOverview
        isLoading={isLoadingCampaignTrackingOverview}
        campaignTrackingOverviewData={campaignTrackingOverviewData}
        t={t}
        onDateGroupOptionChange={onDateGroupOptionChange}
        selectedDateGroupOption={selectedDateGroupOption}
      />
      <Box
        css={{
          margin: 'auto',
          width: '100%',
          maxWidth: '$studyInsights$containerMaxWidth',
          marginBottom: '$4',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <PillGroup items={getPillList(titles)} selectedPill={selectedPill} onClickPill={handleOnClickPill} />
        <Button id="create-campaign-button" onClick={onOpenCreateCampaignModal}>
          {t('add_campaign.modal_title')}
        </Button>
      </Box>
      <CampaignList
        isLoading={isLoadingCampaignList}
        campaigns={campaigns}
        selectedPill={selectedPill}
        t={t}
        onEditCampaign={handleSelectCampaignToEdit}
        onOpenCampaignUrl={handleSelectCampaignUrl}
      />
      <EditCampaignModal
        show={!!selectedCampaignToEdit}
        onClose={handleCloseEditModal}
        onEditCampaign={handleEditCampaignSuccess}
        onErrorEditCampaign={handleEditCampaignError}
        modalTitle={t('edit_campaign.modal_title')}
        values={selectedCampaignToEdit ?? undefined}
      />
      <UrlCampaignModal
        show={!!selectedCampaignToCopyUrl}
        onClose={handleCloseUrlCampaignModal}
        campaignUrl={selectedCampaignToCopyUrl?.campaignUrl ?? ''}
        modalTitle={t('copy_url_modal.title')}
      />
    </Box>
  );
};
